<script lang="ts" setup>
  import { computed, type PropType, watch } from 'vue';
  import { useCountryAddresses } from '@storefront/storefront';
  import { useFormContext, setModelWithContext, useTranslator } from '@othercommerce/foundation';
  import type { Country } from '@storefront/resources';
  import { type Dictionary, type CustomerType, CustomerType as CustomerTypeEnum } from '@othercommerce/domain';

  const translator = useTranslator();

  const defaultLabels = {
    custom: translator.trans('SHOP.COMMON.LABEL.ADDRESS_CUSTOM'),
    countryCode: translator.trans('SHOP.COMMON.LABEL.COUNTRY'),
    type: translator.trans('SHOP.COMMON.LABEL.ADDRESS_TYPE'),
    typePersonal: translator.trans('SHOP.COMMON.LABEL.ADDRESS_TYPE_PERSONAL'),
    typeCompany: translator.trans('SHOP.COMMON.LABEL.ADDRESS_TYPE_COMPANY'),
    name: translator.trans('SHOP.COMMON.LABEL.NAME'),
    company: translator.trans('SHOP.COMMON.LABEL.COMPANY_NAME'),
    street: translator.trans('SHOP.COMMON.LABEL.STREET'),
    buildingNr: translator.trans('SHOP.COMMON.LABEL.BUILDING_NR'),
    apartmentNr: translator.trans('SHOP.COMMON.LABEL.APARTMENT_NR'),
    postcode: translator.trans('SHOP.COMMON.LABEL.POSTCODE'),
    city: translator.trans('SHOP.COMMON.LABEL.CITY'),
    region: translator.trans('SHOP.COMMON.LABEL.REGION'),
    firstLine: translator.trans('SHOP.COMMON.LABEL.ADDRESS_FIRST_LINE'),
    secondLine: translator.trans('SHOP.COMMON.LABEL.ADDRESS_SECOND_LINE'),
    vatId: translator.trans('SHOP.COMMON.LABEL.VAT_ID'),
    email: translator.trans('SHOP.COMMON.LABEL.EMAIL'),
    phone: translator.trans('SHOP.COMMON.LABEL.PHONE'),
  };

  const props = defineProps({
    billable: {
      type: Boolean,
      required: false,
      default: false,
    },
    contactable: {
      type: Boolean,
      required: false,
      default: false,
    },
    contactRequired: {
      type: Boolean,
      required: false,
      default: true,
    },
    externalCountry: {
      type: [String, Boolean] as PropType<string | false>,
      required: false,
      default: false,
    },
    externalType: {
      type: [String, Boolean] as PropType<string | false>,
      required: false,
      default: false,
    },
    named: {
      type: Boolean,
      required: false,
      default: false,
    },
    nameRequired: {
      type: Boolean,
      required: false,
      default: false,
    },
    pointable: {
      type: Boolean,
      required: false,
      default: false,
    },
    customizable: {
      type: Boolean,
      required: false,
      default: false,
    },
    prefix: {
      type: [String, Boolean] as PropType<string | false>,
      required: false,
      default: false,
    },
    typed: {
      type: Boolean,
      required: false,
      default: false,
    },
    countries: {
      type: Array as PropType<Country[]>,
      required: false,
      default: [],
    },
    regions: {
      type: Object as PropType<Dictionary>,
    },
    customerType: {
      type: String as PropType<CustomerType>,
    },
    labels: {
      type: Object as PropType<Partial<typeof defaultLabels>>,
      required: false,
      default: {},
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
  });

  const form = useFormContext();
  const addresses = useCountryAddresses();

  function prefix(name: string) {
    if (props.prefix) {
      return props.prefix + '_' + name;
    } else {
      return name;
    }
  }

  const country = computed(() => {
    if (!form) {
      return undefined;
    }
    return form.data.value[props.externalCountry || prefix('countryCode')];
  });

  // Reset region when user changes country
  watch(() => form?.data.value.countryCode, () => {
    setModelWithContext('region', form, null);
  });

  const type = computed(() => {
    if (!form) {
      return undefined;
    }
    return form.data.value[props.externalType || prefix('type')];
  });

  const isImplicitAddress = computed(() => {
    return addresses.isAddressImplicit(country.value);
  });

  const labels = computed(() => {
    return { ...defaultLabels, ...props.labels };
  });

  const types = computed(() => {
    return [
      { value: 'personal', label: labels.value.typePersonal },
      { value: 'company', label: labels.value.typeCompany },
    ];
  });

  const custom = computed(() => {
    if (props.customizable) {
      if (!form) {
        return undefined;
      }
      return form.data.value[prefix('custom')];
    } else {
      return true;
    }
  });

  const countryRegions = computed(() => {
    if (!country.value || !props.regions || props.customerType === CustomerTypeEnum.MEDICAL) return null;

    return props.regions[country.value];
  });
</script>
<template>
  <section class="address-form">
    <div v-if="externalCountry === false" class="address-form__line">
      <SfFormSelect :label="labels.countryCode" :name="prefix('countryCode')" :options="countries" :readonly="readonly" required />
    </div>
    <div v-if="typed && externalType === false" class="address-form__line address-form__type">
      <SfFormRadio :label="labels.type" :name="prefix('type')" :options="types" :readonly="readonly" />
    </div>
    <slot v-if="customizable" name="custom" />
    <div v-if="customizable" class="address-form__line address-form__custom">
      <SfFormSwitch :label="labels.custom" :name="prefix('custom')" :readonly="readonly" />
    </div>
    <template v-if="custom">
      <template v-if="named">
        <div v-if="!typed || type === 'personal'" class="address-form__line">
          <SfFormInput :label="labels.name" :name="prefix('name')" :readonly="readonly" :required="(typed && type === 'personal') || nameRequired" type="text" />
        </div>
        <div v-if="!typed || type === 'company'" class="address-form__line">
          <SfFormInput :label="labels.company" :name="prefix('company')" :readonly="readonly" :required="typed && type === 'company'" type="text" />
        </div>
      </template>
      <template v-if="isImplicitAddress">
        <div class="address-form__street">
          <SfFormInput :label="labels.street" :name="prefix('street')" :readonly="readonly" required type="text" />
        </div>
        <div class="address-form__line">

          <div class="address-form__building-nr">
            <SfFormInput :label="labels.buildingNr" :name="prefix('buildingNr')" :readonly="readonly" required type="text" />
          </div>
          <div class="address-form__apartment-nr">
            <SfFormInput :label="labels.apartmentNr" :name="prefix('apartmentNr')" :readonly="readonly" type="text" />
          </div>
        </div>
      </template>
      <div v-if="!isImplicitAddress" class="address-form__line">
        <SfFormInput :label="labels.firstLine" :name="prefix('firstLine')" :readonly="readonly" required type="text" />
      </div>
      <div v-if="!isImplicitAddress" class="address-form__line">
        <SfFormInput :label="labels.secondLine" :name="prefix('secondLine')" :readonly="readonly" type="text" />
      </div>
      <div class="address-form__line">
        <div class="address-form__postcode">
          <SfFormInput :label="labels.postcode" :name="prefix('postcode')" :readonly="readonly" required type="text" />
        </div>
        <div class="address-form__city">
          <SfFormInput :label="labels.city" :name="prefix('city')" :readonly="readonly" required type="text" />
        </div>
      </div>
      <div class="address-form__line">
        <SfFormSelect v-if="countryRegions" :label="labels.region" :name="prefix('region')" :options="countryRegions" :readonly="readonly" />

        <SfFormInput v-else :label="labels.region" :name="prefix('region')" :readonly="readonly" />
      </div>
      <div v-if="billable && (!typed || type === 'company')" class="address-form__line">
        <SfFormInput :label="labels.vatId" :name="prefix('vatId')" :readonly="readonly" required type="text" />
      </div>
      <div v-if="contactable" class="address-form__line">
        <SfFormInput :label="labels.email" :name="prefix('email')" :readonly="readonly" :required="contactRequired" type="email" />
      </div>
      <div v-if="contactable" class="address-form__line">
        <SfFormInput :label="labels.phone" :name="prefix('phone')" :readonly="readonly" :required="contactRequired" type="tel" />
      </div>
    </template>
  </section>
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  .address-form {
    display: flex;
    flex-direction: column;
    gap: var(--form-gap-y) var(--form-gap-x);

    &__line {
      display: flex;
      flex-direction: row;
      gap: var(--form-gap-y) var(--form-gap-x);
    }

    &__street,
    &__building-nr,
    &__apartment-nr,
    &__city {

      flex: 1 1 auto;
    }

    &__type {
      margin-bottom: 1rem;
    }

    &__postcode {
      flex: 0 0 208px;
    }
  }

  @include media.query(md) {
    .address-form {
      &__line {
        flex-direction: column;
      }

      &__building-nr,
      &__apartment-nr,
      &__postcode {
        flex: 1 1 auto;
      }
    }
  }
</style>
